import React from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import App from "./App.tsx";
import "./index.css";
import "./styles/prototype.css";
import "./styles/tm-overrides.css";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

// First-party Clerk proxy infrastructure is shipped (vercel.json rewrite +
// optional VITE_USE_CLERK_PROXY=true env flag) but NOT active by default.
//
// Tested 2026-05-15 against the dev tenant: Clerk dev instances reject
// proxied /v1/dev_browser requests with HTTP 500, breaking sign-in.
// Clerk's docs confirm proxyUrl is a production-tier feature. To activate:
//   1. Promote Clerk to a production tenant (Phase B / launch).
//   2. Register the proxy URL in Clerk dashboard → Settings → Domains.
//   3. Set VITE_USE_CLERK_PROXY=true in Vercel env.
// At that point this guard flips ON and cookies become first-party,
// lifting Lighthouse Best-Practices from 78 → 100.
const USE_PROXY = import.meta.env.VITE_USE_CLERK_PROXY === "true";
const PROXY_URL =
  USE_PROXY && typeof window !== "undefined"
    ? `${window.location.origin}/__clerk`
    : undefined;

if (!PUBLISHABLE_KEY) {
  createRoot(document.getElementById("root")!).render(
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      minHeight: "100vh", padding: "2rem", fontFamily: "system-ui, sans-serif",
      background: "#0f0f17", color: "#e2e2ea",
    }}>
      <h1 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>Configuration Error</h1>
      <p style={{ color: "#9999aa", textAlign: "center", maxWidth: 400 }}>
        Missing Clerk Publishable Key. Please check environment variables.
      </p>
    </div>
  );
} else {
  createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        proxyUrl={PROXY_URL}
        afterSignOutUrl="/"
        localization={{
          // Override the tenant-controlled "Sign in to {{applicationName}}" header.
          // The TM Clerk dev tenant has its Application Name set to "app.imdave.ai"
          // in the Clerk dashboard. Until that's renamed via Clerk dashboard (no
          // Backend API for application_name), this localization patches the
          // user-facing text to "Time Miners".
          signIn: {
            start: {
              title: "Sign in to Time Miners",
              subtitle: "Welcome back — let's get you back to the family dashboard.",
            },
          },
          signUp: {
            start: {
              title: "Create your Time Miners account",
              subtitle: "Set up your family's screen-time economy in under two minutes.",
            },
          },
          userButton: { action__signOut: "Sign out" },
        }}
      >
        <App />
      </ClerkProvider>
    </React.StrictMode>
  );

  // Legacy device-id sync removed — pushLocalToSupabase + initFromSupabase
  // were Ollie's pre-Clerk migration helpers that:
  //   • Fired at boot before Clerk session is loaded
  //   • Tried to upsert child_configs / child_states / parent_settings as anon
  //   • RLS-rejected (403) every write because current_user_uuid() was null
  //   • Plus parent_settings has PK on device_id, no UNIQUE on user_id
  // Clerk-bridge hydration in useClerkSupabaseSync now seeds the legitimate
  // localStorage path AFTER auth.users.id resolves, so the legacy IIFE is dead.
}
